import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/config/query-client';

/**
 * The step retry / generate and source-retry endpoints answer 409 when there
 * is nothing to re-run: the part is already generated for this version, or
 * the source never failed. The UI only offers those buttons when they are
 * needed, so a 409 means the screen was stale (or the click landed twice).
 */
export function isStepConflict(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409;
}

/**
 * Handle a 409 calmly: re-fetch what the panel renders from so it shows the
 * real state, and say so with a neutral toast instead of an error.
 *
 * Returns `true` when it handled the error - the caller then skips its own
 * error path (but should still roll back an optimistic "processing" flip).
 */
export function handleStepConflict(
  err: unknown,
  queryClient: QueryClient,
  lessonId: string,
  extraKeys: readonly QueryKey[] = []
): boolean {
  if (!isStepConflict(err)) return false;
  void queryClient.invalidateQueries({
    queryKey: queryKeys.lessons.pipelineStatus(lessonId),
  });
  for (const queryKey of extraKeys) {
    void queryClient.invalidateQueries({ queryKey });
  }
  notify.info(err.detail || 'This is already up to date.');
  return true;
}
